import React from 'react';
import { Helmet } from 'react-helmet';

interface FAQItem {
  question: string;
  answer: string;
}

interface SEOProps {
  title: string;
  description: string;
  image?: string;
  url?: string;
  type?: string;
  keywords?: string[];
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
  };
  schemaType?: 'Organization' | 'WebPage' | 'Article' | 'Service' | 'FAQPage';
  faqs?: FAQItem[];
}

// Organization Schema for the company - Enhanced for AI discovery
const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": "https://www.alioit.com/#organization",
  "name": "Alio IT Solutions",
  "alternateName": ["Alio", "Alio IT", "AlioIT"],
  "legalName": "ALIO IT SOLUTIONS LLC",
  "url": "https://www.alioit.com",
  "logo": {
    "@type": "ImageObject",
    "url": "https://www.alioit.com/alio-logo.svg",
    "width": 200,
    "height": 60
  },
  "image": "https://www.alioit.com/og-image.jpg",
  "description": "Forward-Deployed AI Engineering and nearshore technology consulting for midmarket B2B and regulated industries. Alio embeds senior engineers to ship production AI agents, RAG, and automations—with cloud, cybersecurity, QA, and ManagedOps support across the US, Latin America, and India.",
  "foundingDate": "2011",
  "founder": {
    "@type": "Person",
    "name": "Alfonso Bonillas"
  },
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "9040 S Rita Rd #1270",
    "addressLocality": "Tucson",
    "addressRegion": "AZ",
    "postalCode": "85747",
    "addressCountry": "US"
  },
  "sameAs": [
    "https://www.linkedin.com/company/alio-nearshore-it/",
    "https://x.com/alio_it",
    "https://www.facebook.com/alioits/",
    "https://www.instagram.com/alioit_solutions/",
    "https://www.youtube.com/@alioitsolutions",
    "https://github.com/alioit"
  ],
  "contactPoint": [
    {
    "@type": "ContactPoint",
    "contactType": "sales",
      "email": "info@alioit.com",
      "availableLanguage": ["English", "Spanish", "Portuguese"]
    },
    {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "email": "info@alioit.com",
    "availableLanguage": ["English", "Spanish"]
    }
  ],
  "areaServed": [
    {
      "@type": "Country",
    "name": "United States"
  },
    {
      "@type": "Country", 
      "name": "Mexico"
    },
    {
      "@type": "Country",
      "name": "Canada"
    }
  ],
  "serviceType": [
    "Forward-Deployed AI Engineering",
    "Managed Engineering Operations",
    "AI Agent Development",
    "RAG and LLM Engineering",
    "Custom Software Development",
    "Team Augmentation",
    "DevOps Consulting",
    "QA Automation",
    "Cybersecurity and Virtual CISO",
    "Salesforce Implementation",
    "Cloud Migration",
    "AI and Machine Learning"
  ],
  "knowsAbout": [
    "Forward-Deployed AI Engineering",
    "AI Engineering Consultancy",
    "Production AI Agents",
    "Retrieval Augmented Generation (RAG)",
    "LLM Integration",
    "AI Governance and Compliance",
    "Regulated AI Adoption",
    "Software Development",
    "Cloud Computing",
    "Amazon Web Services (AWS)",
    "Microsoft Azure",
    "Microsoft Purview",
    "Google Cloud Platform",
    "DevOps",
    "CI/CD Pipelines",
    "Kubernetes",
    "Docker",
    "Salesforce",
    "Quality Assurance",
    "Test Automation",
    "Healthcare Interoperability",
    "HIPAA Compliance",
    "SOC 2",
    "FinTech Platforms",
    "EdTech Platforms",
    "Insurance Automation",
    "Legacy Application Modernization",
    "Team Augmentation",
    "Nearshore Development",
    "Agile Methodology",
    "Scrum"
  ],
  "slogan": "Your Remote Team But Closer",
  "numberOfEmployees": {
    "@type": "QuantitativeValue",
    "minValue": 50,
    "maxValue": 200
  }
};

// Website Schema
const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": "https://www.alioit.com/#website",
  "url": "https://www.alioit.com",
  "name": "Alio IT Solutions",
  "description": "Forward-Deployed AI Engineering, ManagedOps, cloud, cybersecurity, and custom software for midmarket and regulated organizations—nearshore teams across Latin America and India.",
  "publisher": {
    "@id": "https://www.alioit.com/#organization"
  },
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://www.alioit.com/?s={search_term_string}",
    "query-input": "required name=search_term_string"
  }
};

export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  image = '/og-image.jpg',
  url = 'https://www.alioit.com',
  type = 'website',
  keywords = [],
  article,
  schemaType = 'WebPage',
  faqs,
}) => {
  const siteTitle = 'Alio IT Solutions';
  const fullTitle = `${title} | ${siteTitle}`;
  const fullUrl = url.startsWith('http') ? url : `https://www.alioit.com${url}`;
  const fullImage = image.startsWith('http') ? image : `https://www.alioit.com${image}`;

  // Generate page-specific schema
  const getPageSchema = () => {
    const baseSchema = {
      "@context": "https://schema.org",
      "@type": schemaType,
      "name": title,
      "description": description,
      "url": fullUrl,
      "image": fullImage,
      "publisher": {
        "@type": "Organization",
        "name": "Alio IT Solutions",
        "logo": {
          "@type": "ImageObject",
          "url": "https://www.alioit.com/alio-logo.svg"
        }
      }
    };

    if (schemaType === 'Article' && article) {
      return {
        ...baseSchema,
        "datePublished": article.publishedTime,
        "dateModified": article.modifiedTime || article.publishedTime,
        "author": {
          "@type": "Person",
          "name": article.author || "Alio IT Solutions"
        }
      };
    }

    if (schemaType === 'Service') {
      return {
        ...baseSchema,
        "@type": "Service",
        "provider": {
          "@type": "Organization",
          "name": "Alio IT Solutions"
        },
        "areaServed": "United States",
        "serviceType": title
      };
    }

    if (schemaType === 'FAQPage' && faqs && faqs.length > 0) {
      return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "name": title,
        "description": description,
        "url": fullUrl,
        "mainEntity": faqs.map((item) => ({
          "@type": "Question",
          "name": item.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": item.answer,
          },
        })),
        "publisher": {
          "@id": "https://www.alioit.com/#organization",
        },
      };
    }

    return baseSchema;
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(', ')} />
      )}
      <meta charSet="utf-8" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:site_name" content="Alio IT Solutions" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />

      {/* Article-specific meta tags */}
      {article?.publishedTime && (
        <meta property="article:published_time" content={article.publishedTime} />
      )}
      {article?.modifiedTime && (
        <meta property="article:modified_time" content={article.modifiedTime} />
      )}
      {article?.author && (
        <meta property="article:author" content={article.author} />
      )}

      {/* Additional Meta Tags */}
      <meta name="theme-color" content="#ffffff" />
      <link rel="canonical" href={fullUrl} />
      
      {/* AI/LLM Discovery */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(getPageSchema())}
      </script>
    </Helmet>
  );
}; 